import { Badge } from '@zuruflex/ui';
import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import type { AppSeen } from '@zuruflex/types';
import { useTranslation } from 'react-i18next';

const PLATFORM: Record<string, string> = { ios: 'iOS', android: 'Android' };

/**
 * Which build of the phone app somebody was last on: `v1.4.0 (37) · iOS`.
 *
 * A build that is not the store one is flagged, because "the customer's
 * phone is on a staging build" is the answer to a whole class of
 * impossible-looking bug reports.
 */
export function AppVersion({ app }: { app: AppSeen | null }) {
  const { t, i18n } = useTranslation('admin');

  if (!app) return <span className="text-xs text-text-muted">{t('app.none')}</span>;

  const build = app.build ? ` (${app.build})` : '';
  const platform = PLATFORM[app.platform] ?? app.platform;
  const preview = app.variant && app.variant !== 'production';

  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={t('app.seen', { when: formatDateTimeIn(app.seenAt, toLocale(i18n.language)) })}
    >
      <Badge variant="outline" className="font-mono">
        v{app.version}
        {build} · {platform}
      </Badge>
      {preview ? <Badge variant="warning">{app.variant}</Badge> : null}
    </span>
  );
}
